"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { ArrowRight, Minus, Plus } from "lucide-react";
import { FileItemIcon } from "@/components/files/FileItemIcon";
import type { FileCategoryNode, FileEntryItem } from "@/lib/files/server";
import type {
  FileCategoryLayoutProps,
  FilesDirectoryLayoutProps,
} from "./types";

const THEME = "var(--association-theme-color-1)";

function CategoryPills({
  tree,
  locale,
  activeSlug,
}: {
  tree: FileCategoryNode[];
  locale: string;
  activeSlug?: string;
}) {
  return (
    <div className="flex flex-wrap justify-center gap-2">
      {tree.map((node) => {
        const active = node.slug === activeSlug;
        return (
          <Link
            key={node.id}
            href={`/${locale}/files/${node.slug}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              active
                ? "text-white shadow-sm"
                : "bg-muted text-foreground hover:bg-muted/80"
            }`}
            style={active ? { backgroundColor: THEME } : undefined}
          >
            {node.title}
          </Link>
        );
      })}
    </div>
  );
}

function FileRow({ file, isAr }: { file: FileEntryItem; isAr: boolean }) {
  const available = file.isAvailable === true && Boolean(file.fileUrl);
  return (
    <li
      className={`flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 shadow-sm ${
        available ? "" : "opacity-80"
      }`}
    >
      <span
        className={`shrink-0 ${available ? "" : "text-muted-foreground"}`}
        style={available ? { color: THEME } : undefined}
      >
        <FileItemIcon className="h-10 w-10" />
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={`truncate text-sm font-medium ${
            available ? "" : "text-muted-foreground"
          }`}
        >
          {file.title}
        </p>
      </div>
      {available ? (
        <a
          href={file.fileUrl!}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold transition hover:opacity-80"
          style={{ color: THEME }}
        >
          {isAr ? "عرض" : "View"}
          <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
        </a>
      ) : null}
    </li>
  );
}

function FileRowList({
  files,
  isAr,
}: {
  files: FileEntryItem[];
  isAr: boolean;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => f.title.toLowerCase().includes(q));
  }, [files, query]);

  return (
    <div className="space-y-4">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={isAr ? "ابحث بالاسم..." : "Search by name..."}
        className="mx-auto block w-full max-w-md rounded-xl border border-border bg-card px-4 py-2.5 text-sm shadow-sm outline-none ring-[var(--association-theme-color-1)] transition focus:ring-2"
      />
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-muted/30 px-6 py-12 text-center text-muted-foreground">
          {isAr ? "لا توجد ملفات." : "No files found."}
        </div>
      ) : (
        <ul className="grid gap-2 rounded-2xl bg-muted/30 p-3 sm:grid-cols-2 sm:p-4">
          {filtered.map((file) => (
            <FileRow key={file.id} file={file} isAr={isAr} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AccordionSection({
  title,
  count,
  files,
  isAr,
  open,
  onToggle,
}: {
  title: string;
  count: number;
  files: FileEntryItem[];
  isAr: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border/70 bg-card">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 bg-[color-mix(in_srgb,var(--association-theme-color-1)_8%,transparent)] px-4 py-3.5 text-start"
      >
        <span className="min-w-0 truncate text-sm font-semibold">{title}</span>
        <span
          className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-foreground/80 px-1.5 text-xs font-semibold text-white"
        >
          {count}
        </span>
        <span
          className="ms-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-foreground"
          aria-hidden
        >
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      {open ? (
        <ul className="grid gap-2 bg-muted/20 p-3 sm:grid-cols-2">
          {files.length === 0 ? (
            <li className="px-2 py-6 text-center text-sm text-muted-foreground sm:col-span-2">
              {isAr ? "لا توجد ملفات." : "No files."}
            </li>
          ) : (
            files.map((file) => <FileRow key={file.id} file={file} isAr={isAr} />)
          )}
        </ul>
      ) : null}
    </div>
  );
}

export function TabsDirectoryLayout({
  tree,
  locale,
  isAr,
}: FilesDirectoryLayoutProps) {
  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <CategoryPills tree={tree} locale={locale} />
      <p className="text-center text-muted-foreground">
        {isAr
          ? "اختر تصنيفاً من الأعلى لعرض الملفات."
          : "Choose a category above to view files."}
      </p>
    </div>
  );
}

export function TabsCategoryLayout({
  tree,
  locale,
  isAr,
  category,
  files,
  childFiles,
  activeNode,
  activeSlug,
}: FileCategoryLayoutProps) {
  const children = activeNode?.children ?? [];
  const hasChildren = children.length > 0;
  const [openSlug, setOpenSlug] = useState<string | null>(
    children[0]?.slug ?? null,
  );

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <CategoryPills tree={tree} locale={locale} activeSlug={activeSlug} />

      <div className="space-y-3">
        <h2 className="text-center text-xl font-bold tracking-tight sm:text-2xl">
          {category.title}
        </h2>
        <span
          aria-hidden
          className="mx-auto block h-1 w-25 rounded-full"
          style={{ backgroundColor: THEME }}
        />
      </div>

      {hasChildren ? (
        <div className="space-y-3">
          {children.map((child) => {
            const childFileList = childFiles[child.slug] ?? [];
            return (
              <AccordionSection
                key={child.id}
                title={child.title}
                count={childFileList.filter((f) => f.isAvailable && f.fileUrl).length}
                files={childFileList}
                isAr={isAr}
                open={openSlug === child.slug}
                onToggle={() =>
                  setOpenSlug((prev) =>
                    prev === child.slug ? null : child.slug,
                  )
                }
              />
            );
          })}
        </div>
      ) : (
        <FileRowList files={files} isAr={isAr} />
      )}
    </div>
  );
}
